import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyBlock } from '@/components/clubs/EmptyBlock';
import {
  ArrowLeft,
  CalendarRange,
  ChevronRight,
  Heart,
  HeartOff,
  MapPin,
  Radio,
  Trophy,
  Users,
} from 'lucide-react';
import {
  addFavoriteClub,
  buildAssetUrl,
  fetchClubBySlug,
  fetchEvents,
  fetchMyFavorites,
  fetchPublicClubTeams,
  friendlyError,
  removeFavoriteClub,
} from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/lib/constants';
import { CLUBS as TID } from '@/constants/testIds';
import { EventCard, EventCardSkeleton } from '@/components/events/EventCard';

function LocationLine({ club }) {
  const parts = [club.city, club.province, club.autonomous_community_or_region, club.country].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <p
      className="inline-flex items-center gap-2 text-sm sm:text-base text-white/85"
      data-testid={TID.detailLocation}
    >
      <MapPin className="h-4 w-4 text-[var(--ns-gold)]" />
      <span>{parts.join(' · ')}</span>
    </p>
  );
}

export default function ClubDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, fetchMe } = useAuth();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [matchesTotal, setMatchesTotal] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // Cargar club por slug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setClub(null);
    (async () => {
      try {
        const res = await fetchClubBySlug(slug);
        if (!cancelled) setClub(res);
      } catch (err) {
        if (!cancelled) {
          const apiErrorCode =
            err?.response?.data?.error ||
            err?.code ||
            err?.payload?.error;
          const isNotFound =
            apiErrorCode === 'club_not_found' || err?.response?.status === 404;
          if (isNotFound) {
            toast.error('Club no encontrado.');
            navigate(ROUTES.clubs, { replace: true });
            return;
          }
          toast.error(friendlyError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  // Cargar estado de favorito
  useEffect(() => {
    if (!isAuthenticated || !club) {
      setFollowing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const favs = await fetchMyFavorites();
        if (!cancelled) {
          setFollowing(favs.some((f) => f.club_id === club.id));
        }
      } catch {
        /* silencioso */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, club]);

  // Cargar los partidos PUBLICADOS del club para la ficha:
  //  • Sólo necesitamos los 2 más recientes para mostrar en las cards.
  //  • El total real (para el contador "X publicados") se toma de `res.total`,
  //    devuelto siempre por el endpoint público de /events. Así el contador
  //    es independiente del `page_size` y no se limita a las cards visibles.
  //  • El backend ya ordena canónicamente por event_date DESC y filtra por
  //    club vía `$or: [club_id, participant_club_id]` (mismo criterio usado
  //    por la página general /partidos?club=<slug>).
  useEffect(() => {
    if (!club) return;
    let cancelled = false;
    setLoadingEvents(true);
    (async () => {
      try {
        const res = await fetchEvents({
          club: club.slug,
          status: 'published',
          page: 1,
          page_size: 2,
          include_counts: false,
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        const total = typeof res?.total === 'number' ? res.total : items.length;
        if (!cancelled) {
          setUpcomingEvents(items);
          setMatchesTotal(total);
        }
      } catch {
        if (!cancelled) {
          setUpcomingEvents([]);
          setMatchesTotal(0);
        }
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => { cancelled = true; };
  }, [club]);

  // Fase 08B: cargar plantillas públicas habilitadas
  const [publicTeams, setPublicTeams] = useState([]);
  const [loadingPublicTeams, setLoadingPublicTeams] = useState(false);
  useEffect(() => {
    if (!club?.slug) return;
    let cancelled = false;
    setLoadingPublicTeams(true);
    (async () => {
      try {
        const d = await fetchPublicClubTeams(club.slug);
        if (!cancelled) setPublicTeams(d?.items || []);
      } catch {
        if (!cancelled) setPublicTeams([]);
      } finally {
        if (!cancelled) setLoadingPublicTeams(false);
      }
    })();
    return () => { cancelled = true; };
  }, [club?.slug]);

  const handleFollowToggle = async () => {
    if (!club) return;
    if (!isAuthenticated) {
      navigate(ROUTES.login, {
        state: { from: window.location.pathname },
      });
      return;
    }
    setBusy(true);
    try {
      if (following) {
        const { message } = await removeFavoriteClub(club.id);
        setFollowing(false);
        toast.success(message || 'Has dejado de seguir el club.');
      } else {
        const { message } = await addFavoriteClub(club.id);
        setFollowing(true);
        toast.success(message || 'Club añadido a favoritos.');
      }
      // refrescar usuario para que favorite_club_id quede sincronizado
      if (typeof fetchMe === 'function') await fetchMe();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const teams = club?.teams || [];
  const sponsors = club?.sponsors || [];
  const locationLine = useMemo(
    () => [club?.city, club?.province].filter(Boolean).join(', '),
    [club]
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={TID.detailPage}>
          <Skeleton className="h-56 sm:h-72 w-full rounded-2xl" />
          <div className="mt-6 space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </MainLayout>
    );
  }
  if (!club) return null;

  return (
    <MainLayout>
      <div data-testid={TID.detailPage} className="pb-12">
        {/* Hero — cabecera moderna sobre imagen de portada */}
        <section
          className="relative w-full min-h-[360px] sm:min-h-[440px] lg:min-h-[500px] overflow-hidden bg-gradient-to-br from-[var(--ns-gold)]/40 to-[var(--ns-gold-700)]/30"
          data-testid={TID.detailHero}
        >
          {club.cover_image_url ? (
            <img
              src={club.cover_image_url}
              alt={`Portada ${club.name}`}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : null}
          {/* Overlays: vertical para legibilidad de texto + lateral para el back button */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-transparent to-transparent hidden sm:block" aria-hidden="true" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full min-h-[360px] sm:min-h-[440px] lg:min-h-[500px] flex flex-col">
            {/* Top bar: volver + acción de seguir (siempre visible en el hero) */}
            <div className="flex items-start justify-between gap-3 pt-4 sm:pt-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(ROUTES.clubs)}
                className="text-white/90 hover:text-white hover:bg-white/15 h-8 px-2 backdrop-blur-sm"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Volver a Clubes
              </Button>

              {isAuthenticated ? (
                <div className="flex items-center gap-2">
                  {following ? (
                    <Badge
                      className="hidden sm:inline-flex bg-white/15 backdrop-blur-md text-white border border-white/30"
                      data-testid={TID.detailFollowingBadge}
                    >
                      <Heart className="h-3.5 w-3.5 mr-1 fill-[var(--ns-gold)] text-[var(--ns-gold)]" />
                      Sigues a este club
                    </Badge>
                  ) : null}
                  <Button
                    onClick={handleFollowToggle}
                    disabled={busy}
                    size="lg"
                    className={
                      following
                        ? 'h-11 px-5 rounded-xl bg-white/15 backdrop-blur-md text-white border border-white/40 hover:bg-white/25 shadow-[0_8px_24px_rgba(0,0,0,0.35)]'
                        : 'h-11 px-6 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-colors shadow-[0_10px_30px_rgba(0,0,0,0.45)]'
                    }
                    data-testid={TID.detailFollowBtn}
                  >
                    {following ? (
                      <>
                        <HeartOff className="h-4 w-4 mr-2" /> Dejar de seguir
                      </>
                    ) : (
                      <>
                        <Heart className="h-4 w-4 mr-2" /> Seguir club
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  asChild
                  size="lg"
                  className="h-11 px-6 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-colors shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
                  data-testid={TID.detailLoginCta}
                >
                  <Link to={ROUTES.login} state={{ from: `/clubes/${slug}` }}>
                    <Heart className="h-4 w-4 mr-2" /> Seguir club
                  </Link>
                </Button>
              )}
            </div>

            {/* Identidad del club (logo + deporte + nombre + ubicación) */}
            <div className="mt-auto pb-4 sm:pb-6 flex items-end gap-4 sm:gap-6">
              <div
                className="h-24 w-24 sm:h-32 sm:w-32 lg:h-36 lg:w-36 rounded-2xl bg-white border-2 border-white/80 shadow-[0_12px_32px_rgba(0,0,0,0.45)] overflow-hidden flex items-center justify-center shrink-0 p-2 sm:p-3"
                data-testid={TID.detailLogo}
              >
                {club.logo_url ? (
                  <img
                    src={buildAssetUrl(club.logo_url)}
                    alt={`Logo ${club.name}`}
                    className="max-h-[78%] max-w-[78%] object-contain"
                    draggable={false}
                  />
                ) : (
                  <Trophy className="h-10 w-10 text-[var(--ns-gold-600)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {club.sport_name ? (
                  <Badge
                    variant="secondary"
                    className="bg-white/95 text-[var(--ns-text)] uppercase tracking-wider text-[10px] sm:text-xs border border-white/40"
                  >
                    {club.sport_name}
                  </Badge>
                ) : null}
                <h1
                  className="mt-2 font-display text-2xl sm:text-4xl lg:text-5xl text-white leading-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)]"
                  data-testid={TID.detailName}
                >
                  {club.name}
                </h1>
                <div className="mt-2">
                  <LocationLine club={club} />
                </div>
              </div>
            </div>

            {/* Contadores dinámicos — usan los mismos datos ya cargados */}
            <div
              className="pb-4 sm:pb-6 grid grid-cols-3 gap-2 sm:gap-3"
              data-testid={TID.detailStats || 'club-hero-stats'}
            >
              {[
                { key: 'teams', label: teams.length === 1 ? 'Equipo' : 'Equipos', value: teams.length, Icon: Users },
                { key: 'sponsors', label: sponsors.length === 1 ? 'Patrocinador' : 'Patrocinadores', value: sponsors.length, Icon: Trophy },
                { key: 'matches', label: matchesTotal === 1 ? 'Partido' : 'Partidos', value: matchesTotal, Icon: CalendarRange },
              ].map(({ key, label, value, Icon }) => (
                <div
                  key={key}
                  data-testid={`club-hero-stat-${key}`}
                  className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-3"
                >
                  <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg bg-white/15 border border-white/25 inline-flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[var(--ns-gold)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-lg sm:text-xl font-semibold text-white leading-tight tabular-nums">
                      {value}
                    </div>
                    <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-white/70 truncate">
                      {label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Columna principal */}
            <div className="lg:col-span-2 space-y-10">
              {/* Descripción */}
              {club.description ? (
                <article data-testid={TID.detailDescription}>
                  <h2 className="font-display text-xl text-[var(--ns-text)]">Sobre el club</h2>
                  <p className="mt-3 text-[var(--ns-text-muted)] leading-relaxed">
                    {club.description}
                  </p>
                </article>
              ) : null}

              {/* Fase 08B — Las plantillas públicas se muestran ahora
                  directamente como extras dentro de "Equipos / categorías"
                  (columna derecha). No mostramos un bloque independiente. */}

              {/* Partidos del club: sólo los 2 más recientes publicados
                  (fecha desc). Usa el mismo EventCard que /partidos. Debajo
                  se ofrece el enlace "Ver todos los partidos" que abre la
                  página general con el club pre-seleccionado como filtro. */}
              <article>
                <header className="flex items-baseline justify-between mb-4">
                  <h2 className="font-display text-xl text-[var(--ns-text)]">Partidos</h2>
                  {matchesTotal > 0 ? (
                    <span
                      className="text-xs text-[var(--ns-text-subtle)]"
                      data-testid="club-matches-count"
                    >
                      {matchesTotal} publicado{matchesTotal === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </header>
                {loadingEvents ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EventCardSkeleton />
                    <EventCardSkeleton />
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <EmptyBlock
                    icon={CalendarRange}
                    title="Aún no hay partidos publicados"
                    description="En cuanto el club publique partidos en NetScouters, aparecerán aquí."
                    testId={TID.detailEventsEmpty}
                  />
                ) : (
                  <>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                      data-testid="club-matches-grid"
                    >
                      {upcomingEvents.map((ev) => (
                        <EventCard key={ev.id} event={ev} />
                      ))}
                    </div>
                    <div className="mt-5 flex justify-center">
                      <Button
                        asChild
                        variant="outline"
                        className="h-10 rounded-xl border-[var(--ns-border)] text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)] px-5"
                      >
                        <Link
                          to={`${ROUTES.events}?club=${club.slug}&status=published`}
                          data-testid="club-matches-see-all"
                        >
                          Ver todos los partidos
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </>
                )}
              </article>

              {/* Directos */}
              <article>
                <header className="flex items-baseline justify-between mb-4">
                  <h2 className="font-display text-xl text-[var(--ns-text)]">Directos</h2>
                  <span className="text-xs text-[var(--ns-text-subtle)]">F03</span>
                </header>
                <EmptyBlock
                  icon={Radio}
                  title="No hay directos en este momento"
                  description="Cuando arranque una emisión en directo, verás aquí el acceso."
                  testId={TID.detailLivesEmpty}
                />
              </article>
            </div>

            {/* Sidebar: Equipos + Patrocinadores */}
            <aside className="space-y-8">
              <section data-testid={TID.detailTeamsSection}>
                <h3 className="font-display text-lg text-[var(--ns-text)] mb-3">
                  Equipos / categorías
                </h3>
                {teams.length === 0 ? (
                  <EmptyBlock
                    icon={Users}
                    title="Sin equipos publicados"
                    description="Pronto se incorporarán las categorías del club."
                  />
                ) : (
                  <ul className="space-y-2">
                    {teams.map((t) => {
                      // Fase 08B: la ficha de plantilla por equipo se muestra
                      // INDIVIDUALMENTE dentro de cada tarjeta. Sin avisos globales.
                      const publicMeta = publicTeams.find((pt) => pt.id === t.id);
                      const hasPublicRoster = !!publicMeta && (publicMeta.visible_players_count || 0) > 0;
                      return (
                        <li
                          key={t.id}
                          data-testid={TID.detailTeamItem(t.id)}
                          className="flex flex-col gap-2 p-3 rounded-xl bg-white border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[var(--ns-text)] truncate">
                              {t.name}
                            </p>
                            <p className="text-xs text-[var(--ns-text-muted)] capitalize">
                              {t.category}
                              {t.season ? ` · ${t.season}` : ''}
                            </p>
                            {hasPublicRoster ? (
                              <p
                                data-testid={`club-team-roster-available-${t.id}`}
                                className="text-[11px] text-[var(--ns-gold-700)] font-semibold mt-1.5 inline-flex items-center gap-1"
                              >
                                <Users className="h-3 w-3" />
                                Plantilla disponible · {publicMeta.visible_players_count} {publicMeta.visible_players_count === 1 ? 'jugador' : 'jugadores'}
                              </p>
                            ) : (
                              <p
                                data-testid={`club-team-roster-unavailable-${t.id}`}
                                className="text-[11px] text-[var(--ns-text-subtle)] mt-1.5"
                              >
                                Plantilla no disponible
                              </p>
                            )}
                          </div>
                          {hasPublicRoster ? (
                            <Button
                              asChild
                              size="sm"
                              data-testid={`club-team-view-roster-${publicMeta.slug || t.id}`}
                              className="h-8 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0 w-fit"
                            >
                              <Link to={`/clubes/${club.slug}/equipos/${publicMeta.slug}`} className="inline-flex items-center gap-1">
                                Ver plantilla <ChevronRight className="h-3.5 w-3.5" />
                              </Link>
                            </Button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section data-testid={TID.detailSponsorsSection}>
                <h3 className="font-display text-lg text-[var(--ns-text)] mb-3">Patrocinadores</h3>
                {sponsors.length === 0 ? (
                  <EmptyBlock
                    icon={Trophy}
                    title="Sin patrocinadores visibles"
                    description="El club todavía no muestra patrocinadores públicos."
                  />
                ) : (
                  <ul className="space-y-2">
                    {sponsors.map((s) => (
                      <li
                        key={s.id}
                        data-testid={TID.detailSponsorItem(s.id)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[var(--ns-border)]"
                      >
                        <div className="h-9 w-9 rounded-lg bg-[var(--ns-bg-2)] inline-flex items-center justify-center text-[var(--ns-gold-700)] text-xs font-bold uppercase shrink-0">
                          {(s.name || '?').slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          {s.website_url ? (
                            <a
                              href={s.website_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm font-medium text-[var(--ns-text)] hover:text-[var(--ns-gold-700)] truncate block"
                            >
                              {s.name}
                            </a>
                          ) : (
                            <span className="text-sm font-medium text-[var(--ns-text)] truncate block">
                              {s.name}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </aside>
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
